/**
 * CashToken stablecoin support (proof-of-concept).
 *
 * BCH stablecoins like ParyonUSD (PUSD) are *fungible CashTokens* — native BCH
 * mainnet assets that live on the same blockchain and addresses we already
 * watch via Fulcrum. Accepting them requires three things:
 *
 *   1. A TOKEN-AWARE receiving address (z… / r…), because token-aware wallets
 *      refuse to send tokens to plain q…/p… addresses (CHIP-2022-02 safety rule).
 *   2. A payment URI carrying the token category + amount:
 *        bitcoincash:z…?c=<category_hex>&f=<fungible_amount>&s=<sats>
 *   3. Detection via Fulcrum `listunspent(scripthash, "tokens_only")`, matching
 *      the returned `token_data.category` + (BigInt) `token_data.amount`.
 *
 * This module holds the stablecoin registry + URI/amount helpers. The address
 * conversion and Fulcrum query live in ./fulcrum.ts.
 */

import { toTokenAwareAddress } from '@/lib/fulcrum';

export interface Stablecoin {
  /** Short ticker, e.g. "PUSD". */
  symbol: string;
  /** Human name. */
  name: string;
  /**
   * CashToken category id, big-endian (display) hex — exactly as published by
   * the issuer. This is what Fulcrum returns in `token_data.category`, so the
   * two compare directly (case-insensitive).
   */
  category: string;
  /** On-chain decimal places. PUSD = 2 (base unit 0.01 = one cent). */
  decimals: number;
  /** Peg currency, for display/pricing (PUSD ≈ 1 USD). */
  peggedTo: string;
  /**
   * Token icon URL, sourced from the issuer's BCMR registry (`uris.icon`).
   * Used purely for display polish on payment/receipt screens.
   */
  iconUrl?: string;
}

/** ParyonUSD — https://paryonusd.com (fungible CashToken, BCH mainnet). */
export const PUSD: Stablecoin = {
  symbol: 'PUSD',
  name: 'ParyonUSD',
  category: '2469acc5afa4b10cb5b5c04afb89c3a3ffd61c5da9c01e26d00951cae2a02544',
  decimals: 2,
  peggedTo: 'USD',
  // Canonical icon from the PUSD BCMR registry (bcmr.paytaca.com → uris.icon).
  iconUrl: 'https://paryonusd.com/logo.svg',
};

export const STABLECOINS: Stablecoin[] = [PUSD];

/**
 * Dust satoshis that must accompany a token output. Tokens cannot exist without
 * a tiny BCH amount on the UTXO; ~1000 sats is a safe, standard value for a
 * token-aware P2PKH output. The sending wallet actually decides the final value;
 * `s` is a hint/floor.
 */
export const TOKEN_DUST_SATS = 1000;

/**
 * Convert a display amount (e.g. 5 for $5.00) into on-chain fungible base units.
 * For PUSD (2 decimals): 5 → 500n. Uses BigInt because token amounts can exceed
 * JS safe-integer range (max 2^63-1).
 *
 * `displayAmount` is given in whole peg units (dollars). We round to the token's
 * decimal precision.
 */
export function toBaseUnits(displayAmount: number, coin: Stablecoin): bigint {
  if (!(displayAmount > 0)) return 0n;
  const factor = 10 ** coin.decimals;
  // Round to nearest base unit to avoid FP dust (e.g. 19.99 * 100 = 1998.9999…).
  const units = Math.round(displayAmount * factor);
  return BigInt(units);
}

/** Format on-chain base units back to a human display string, e.g. 500n → "5.00". */
export function fromBaseUnits(units: bigint, coin: Stablecoin): string {
  const factor = BigInt(10 ** coin.decimals);
  const whole = units / factor;
  const frac = units % factor;
  if (coin.decimals === 0) return whole.toString();
  return `${whole.toString()}.${frac.toString().padStart(coin.decimals, '0')}`;
}

/**
 * Build a CashToken payment URI (per CHIP-paypro / BIP21-CashTokens):
 *
 *   bitcoincash:<token-aware-addr>?c=<category>&f=<base_units>&s=<sats>
 *
 * The address is force-converted to its token-aware (z…/r…) form so wallets
 * will accept the token request. `label`/`message` are added for UX if given.
 */
export function generateTokenUri(
  address: string,
  coin: Stablecoin,
  displayAmount: number,
  opts: { label?: string; sats?: number } = {}
): string {
  const tokenAddr = toTokenAwareAddress(address); // includes `bitcoincash:` prefix
  const units = toBaseUnits(displayAmount, coin);
  const sats = opts.sats ?? TOKEN_DUST_SATS;

  const params = new URLSearchParams();
  params.set('c', coin.category);
  params.set('f', units.toString());
  params.set('s', String(sats));
  if (opts.label) params.set('label', opts.label);

  // URLSearchParams encodes commas/spaces; that's fine for label. The category
  // and numeric params are plain hex/digits so they pass through unchanged.
  return `${tokenAddr}?${params.toString()}`;
}

/**
 * Look up a stablecoin by its category id (case-insensitive). Returns undefined
 * if we don't recognize it.
 */
export function findStablecoinByCategory(category: string): Stablecoin | undefined {
  const c = category.toLowerCase();
  return STABLECOINS.find((s) => s.category.toLowerCase() === c);
}
